import { PointerEvent as ReactPointerEvent, useCallback, useRef } from "react";

import { type DockDragSource, useDockDragController } from "./dockDragContext";

const DRAG_THRESHOLD_PX = 4;
/** Interactive controls inside the chrome don't initiate a drag. */
const INTERACTIVE_SELECTOR = "button, input, textarea, select, [contenteditable]";
/** Public SDK contract — see `WindowProps.header`. */
const DRAG_HANDLE_SELECTOR = ".window-header";

interface DragGesture {
    startX: number;
    startY: number;
    active: boolean;
    moveHandler: (ev: PointerEvent) => void;
    upHandler: (ev: PointerEvent) => void;
}

export interface UseDockDragSourceArgs {
    source: DockDragSource;
    windowId: string;
    /** Pointerdowns whose target matches this selector short-circuit the gesture. */
    ignoreTargetSelector?: string;
    /** Runs once when the threshold is crossed, *before* `controller.beginDrag`. */
    onActivate?: (ev: PointerEvent) => void;
    /** Runs on every post-activation pointermove, before `controller.updatePointer`. */
    onMove?: (ev: PointerEvent) => void;
    /** Runs on release inside the zone, before `controller.endDrag`. */
    onDropInZone?: (insertionIndex: number, ev: PointerEvent) => void;
    /** Runs on release outside the zone (or with no resolved index), before `endDrag`. */
    onDropOutsideZone?: (ev: PointerEvent) => void;
}

export interface UseDockDragSourceResult {
    onPointerDown: (e: ReactPointerEvent<HTMLElement>) => void;
}

/**
 * Shared pointer-gesture wiring for both dock drag sources. Owns the pointerdown
 * filter, threshold activation, document-level move/up/cancel, and drop classification.
 * Callbacks live in a ref, so `onPointerDown` is identity-stable across renders.
 */
export function useDockDragSource({
    source,
    windowId,
    ignoreTargetSelector,
    onActivate,
    onMove,
    onDropInZone,
    onDropOutsideZone,
}: UseDockDragSourceArgs): UseDockDragSourceResult {
    const controller = useDockDragController();
    const dragRef = useRef<DragGesture | null>(null);

    const callbacksRef = useRef({ onActivate, onMove, onDropInZone, onDropOutsideZone });
    callbacksRef.current = { onActivate, onMove, onDropInZone, onDropOutsideZone };

    const cleanup = useCallback(() => {
        const drag = dragRef.current;
        if (!drag) {
            return;
        }
        document.removeEventListener("pointermove", drag.moveHandler);
        document.removeEventListener("pointerup", drag.upHandler);
        document.removeEventListener("pointercancel", drag.upHandler);
        dragRef.current = null;
    }, []);

    const onPointerDown = useCallback(
        (e: ReactPointerEvent<HTMLElement>) => {
            const target = e.target as HTMLElement | null;
            if (!target) {
                return;
            }
            if (target.closest(INTERACTIVE_SELECTOR)) {
                return;
            }
            if (!target.closest(DRAG_HANDLE_SELECTOR)) {
                return;
            }
            if (e.button !== 0) {
                return;
            }
            if (ignoreTargetSelector && target.closest(ignoreTargetSelector)) {
                return;
            }

            const startX = e.clientX;
            const startY = e.clientY;

            const moveHandler = (ev: PointerEvent) => {
                const drag = dragRef.current;
                if (!drag) {
                    return;
                }
                if (!drag.active) {
                    if (Math.hypot(ev.clientX - drag.startX, ev.clientY - drag.startY) < DRAG_THRESHOLD_PX) {
                        return;
                    }
                    drag.active = true;
                    callbacksRef.current.onActivate?.(ev);
                    controller.beginDrag(source, windowId);
                    controller.updatePointer(ev.clientX, ev.clientY);
                    return;
                }
                callbacksRef.current.onMove?.(ev);
                controller.updatePointer(ev.clientX, ev.clientY);
            };

            const upHandler = (ev: PointerEvent) => {
                const drag = dragRef.current;
                cleanup();
                if (!drag || !drag.active) {
                    return;
                }
                // Read live state via getState(); the rendered snapshot is stale here.
                const dragState = controller.getState();
                if (dragState.isInZone && dragState.insertionIndex != null) {
                    callbacksRef.current.onDropInZone?.(dragState.insertionIndex, ev);
                } else {
                    callbacksRef.current.onDropOutsideZone?.(ev);
                }
                controller.endDrag();
            };

            dragRef.current = { startX, startY, active: false, moveHandler, upHandler };
            document.addEventListener("pointermove", moveHandler);
            document.addEventListener("pointerup", upHandler);
            document.addEventListener("pointercancel", upHandler);
        },
        [cleanup, controller, source, windowId, ignoreTargetSelector],
    );

    return { onPointerDown };
}
